import { createThemePreset } from '@/styles/theme/themeSchema'
import { marketDark, marketLight } from '@/styles/theme/presets/energyMarketColors'

export const operatorBrandColors = {
  liander: '#0077A1',
  stedin: '#FFD100',
  enexis: '#DF0073',
  westland: '#EE3129',
  rendo: '#009E58',
  coteq: '#151C64',
  // TenneT's brand blue: the wordmark fill in TenneT's own logo file (PI-659).
  tennet: '#004B8D',
};

// Congestion direction (PI-659). Aliases of the palette, so they follow light and
// dark mode; "both" has no CSS-token: it is drawn as a split of these two.
const congestionColors = {
  feedIn: 'var(--color-palette-amber)',
  offtake: 'var(--color-palette-blue)',
};

const light = {
  color: {
    operator: operatorBrandColors,
    congestion: congestionColors,
    brand: {
      // Anthropic brand accent; https://github.com/anthropics/skills/tree/main/skills/brand-guidelines
      claude: '217 119 87',
      // Brand green = emerald-500; hover steps darker to emerald-600. Matches
      // palette.emerald so there is one brand green across UI and charts.
      primary: '16 185 129',
      primaryHover: '5 150 105',
      primarySoft: '236 253 245',
      accent: '37 99 235',
      accentSoft: '239 246 255',
    },
    bg: {
      app: '248 250 252',
      subtle: '241 245 249',
      canvas: '255 255 255',
    },
    surface: {
      base: '255 255 255',
      raised: '248 250 252',
      elevated: '255 255 255',
      inverse: '15 23 42',
    },
    border: {
      subtle: '226 232 240',
      default: '203 213 225',
      strong: '148 163 184',
    },
    text: {
      primary: '15 23 42',
      secondary: '71 85 105',
      muted: '100 116 139',
      inverse: '255 255 255',
    },
    state: {
      success: '16 185 129',
      warning: '217 119 6',
      danger: '220 38 38',
      info: '37 99 235',
      focus: '16 185 129',
    },
    palette: {
      red: '239 68 68',
      rose: '225 29 72',
      orange: '234 88 12',
      amber: '245 158 11',
      yellow: '234 179 8',
      lime: '132 204 22',
      green: '34 197 94',
      emerald: '16 185 129',
      teal: '20 184 166',
      cyan: '6 182 212',
      sky: '14 165 233',
      blue: '37 99 235',
      indigo: '99 102 241',
      violet: '139 92 246',
      purple: '168 85 247',
      fuchsia: '217 70 239',
      pink: '236 72 153',
    },
    market: marketLight,
    auth: {
      brandStart: '8 14 26',
      brandMid: '8 14 26',
      brandEnd: '11 18 32',
      brandDivider: '56 189 248',
      formStart: '255 255 255',
      formMid: '255 255 255',
      formEnd: '255 255 255',
      formDivider: '203 213 225',
      formShadow: '15 23 42',
    },
    shadow: '15 23 42',
    overlay: {
      scrim: '15 23 42',
      brand: '16 185 129',
      accent: '37 99 235',
    },
    static: {
      black: '0 0 0',
      white: '255 255 255',
    },
  },
  radius: {
    xs: '0.375rem',
    sm: '0.5rem',
    md: '0.75rem',
    lg: '1rem',
    xl: '1.25rem',
    pill: '9999px',
  },
  space: {
    1: '0.25rem',
    2: '0.5rem',
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    controlXXs: '0.5rem',
    controlXSm: '0.625rem',
    controlXMd: '0.875rem',
    controlXLg: '1rem',
    controlYXs: '0.25rem',
    controlYSm: '0.375rem',
    controlYMd: '0.625rem',
    controlYLg: '0.75rem',
    card: '1rem',
    section: '1.5rem',
    dialog: '1.25rem',
    page: '1.5rem',
  },
  size: {
    controlXs: '1.75rem',
    controlSm: '2rem',
    controlMd: '2.5rem',
    controlLg: '3rem',
    iconSm: '1rem',
    iconMd: '1.125rem',
    iconLg: '1.25rem',
    header: '4rem',
    sidebar: '15rem',
  },
  shadow: {
    card: '0 1px 1px rgba(15, 23, 42, 0.04), 0 4px 8px -2px rgba(15, 23, 42, 0.06), 0 12px 24px -8px rgba(15, 23, 42, 0.06)',
    cockpitCard: '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 16px -8px rgba(15, 23, 42, 0.08)',
    elevated: '0 2px 4px rgba(15, 23, 42, 0.05), 0 12px 24px -6px rgba(15, 23, 42, 0.10), 0 24px 48px -12px rgba(15, 23, 42, 0.10)',
    dialog: '0 20px 60px rgba(15, 23, 42, 0.18)',
    focus: '0 0 0 4px rgba(16, 185, 129, 0.14)',
  },
  glow: {
    primary: '0 0 0 1px rgba(16, 185, 129, 0.18), 0 8px 24px -6px rgba(16, 185, 129, 0.28), 0 0 12px rgba(16, 185, 129, 0.12)',
    accent: '0 0 0 1px rgba(37, 99, 235, 0.16), 0 8px 24px -6px rgba(37, 99, 235, 0.24), 0 0 12px rgba(37, 99, 235, 0.10)',
  },
  font: {
    sans: 'Satoshi, Inter, ui-sans-serif, system-ui, sans-serif',
    display: 'Azonix, Satoshi, Inter, ui-sans-serif, sans-serif',
  },
}

const dark = {
  color: {
    operator: operatorBrandColors,
    congestion: congestionColors,
    brand: {
      // Anthropic brand accent; https://github.com/anthropics/skills/tree/main/skills/brand-guidelines
      claude: '217 119 87',
      primary: '52 211 153',
      primaryHover: '16 185 129',
      primarySoft: '5 46 43',
      accent: '56 189 248',
      accentSoft: '16 42 67',
    },
    bg: {
      app: '8 14 26',
      subtle: '11 18 32',
      canvas: '13 23 38',
    },
    surface: {
      base: '15 26 43',
      raised: '20 35 58',
      elevated: '26 43 74',
      inverse: '255 255 255',
    },
    border: {
      subtle: '30 41 59',
      default: '51 65 85',
      strong: '100 116 139',
    },
    text: {
      primary: '248 250 252',
      // Dark-mode ramp runs brighter than the light ramp so secondary/muted
      // subtitles & captions stay readable on the near-black app background.
      secondary: '209 217 230',
      muted: '184 197 213',
      inverse: '15 23 42',
    },
    state: {
      success: '52 211 153',
      warning: '251 191 36',
      danger: '248 113 113',
      info: '56 189 248',
      focus: '52 211 153',
    },
    palette: {
      red: '248 113 113',
      rose: '251 113 133',
      orange: '251 146 60',
      amber: '251 191 36',
      yellow: '250 204 21',
      lime: '163 230 53',
      green: '74 222 128',
      emerald: '52 211 153',
      teal: '45 212 191',
      cyan: '34 211 238',
      sky: '56 189 248',
      blue: '56 189 248',
      indigo: '129 140 248',
      violet: '167 139 250',
      purple: '192 132 252',
      fuchsia: '232 121 249',
      pink: '244 114 182',
    },
    market: marketDark,
    auth: {
      brandStart: '8 14 26',
      brandMid: '8 14 26',
      brandEnd: '11 18 32',
      brandDivider: '56 189 248',
      formStart: '20 35 58',
      formMid: '15 26 43',
      formEnd: '13 23 38',
      formDivider: '56 189 248',
      formShadow: '0 0 0',
    },
    shadow: '0 0 0',
    overlay: {
      scrim: '0 0 0',
      brand: '52 211 153',
      accent: '56 189 248',
    },
    static: {
      black: '0 0 0',
      white: '255 255 255',
    },
  },
  radius: light.radius,
  space: light.space,
  size: light.size,
  shadow: {
    card: '0 12px 32px rgba(0, 0, 0, 0.35)',
    cockpitCard: '0 12px 26px rgba(0, 0, 0, 0.28)',
    elevated: '0 18px 48px rgba(0, 0, 0, 0.45)',
    dialog: '0 24px 72px rgba(2, 6, 23, 0.48)',
    focus: '0 0 0 4px rgba(52, 211, 153, 0.18)',
  },
  glow: {
    primary: '0 0 0 1px rgba(52, 211, 153, 0.28), 0 8px 28px -4px rgba(52, 211, 153, 0.30), 0 0 18px rgba(52, 211, 153, 0.16)',
    accent: '0 0 0 1px rgba(56, 189, 248, 0.26), 0 8px 28px -4px rgba(56, 189, 248, 0.26), 0 0 18px rgba(56, 189, 248, 0.14)',
  },
  font: light.font,
}

export default createThemePreset({
  id: 'profiteia',
  label: 'Profiteia',
  modes: {
    light,
    dark,
  },
})
